import React, { useEffect, useState } from "react";

import { addViewportIsMobileListener, isNotUndefined } from "@/shared/FunctionCore";
import { PaginationElementComponentProps, AllPropertiesRequired } from "@/components/PaginationElementComponent/interfaces";
import {useTranslation} from "react-i18next";
import "@/components/PaginationElementComponent/PaginationElementComponentStyles.module.scss";

const separator = -999;

const range = (start: number, end: number): number[] => window.Array.from({ "length": end - start }, (_, nu) => nu + start);

const getPaginationValues = (
	currentPage: number,
	siblings: number,
	totalPages: number,
	isMobileWidth: boolean): number[] => {
	const totalPageNumbers = siblings + 5;

	if (totalPageNumbers >= totalPages) {
		return range(1, totalPages + 1);
	}

	const leftSiblingIndex = window.Math.max(currentPage - siblings, 1);
	const rightSiblingIndex = window.Math.min(currentPage + siblings, totalPages);

	const shouldShowLeftDots = leftSiblingIndex >= 2;
	const shouldShowRightDots = rightSiblingIndex <= totalPages - 2;

	if (!shouldShowLeftDots && shouldShowRightDots) {
		const leftItemCount = 2 * siblings;

		const leftRange = range(1, leftItemCount + 1);

		const leftRangeToUse = isMobileWidth ? range(1, leftItemCount) : leftRange;

		return [...leftRangeToUse, separator];
	}

	if (shouldShowLeftDots && !shouldShowRightDots) {
		const rightItemCount = 2 * siblings;
		const rightRange = range(totalPages - rightItemCount + 1, totalPages + 1);

		return [separator, ...rightRange];
	}

	if (shouldShowLeftDots && shouldShowRightDots) {
		const middleRange = range(leftSiblingIndex, rightSiblingIndex);

		const middleRangeToUse = isMobileWidth ? range(leftSiblingIndex + 1, rightSiblingIndex) : middleRange;

		return [separator, ...middleRangeToUse, separator];
	}

	return [];
};

const PaginationElementComponent = (props: PaginationElementComponentProps): JSX.Element => {
	const {
		currentPage,
		currentPageValueClassName,
		ellipsesClassName,
		nextButtonClassName,
		nextClassName,
		pageSize,
		paginationContainerRef,
		previousButtonClassName,
		previousClassName,
		siblings,
		totalPages,
		wrapperClassName
	} = props;
	const {t} = useTranslation();

	const propsWithDefaults: AllPropertiesRequired<PaginationElementComponentProps> = {
		...props,
		"currentPage": isNotUndefined(currentPage) ? currentPage : 1,
		"pageSize": isNotUndefined(pageSize) ? pageSize : 50,
		"siblings": isNotUndefined(siblings) ? siblings : 1,
		"totalPages": isNotUndefined(totalPages) ? totalPages : 20,
	};

	const [isMobileWidth, setIsMobileWidth] = useState(false);

	useEffect(() => {
		addViewportIsMobileListener((isMobile) => setIsMobileWidth(isMobile));
	}, []);

	const paginationValues = getPaginationValues(propsWithDefaults.currentPage, propsWithDefaults.siblings, propsWithDefaults.totalPages, isMobileWidth);

	if (propsWithDefaults.currentPage === 0 || paginationValues.length < 2) {
		return <></>;
	}

	return (
		<div
			className={wrapperClassName}
			ref={paginationContainerRef}>
			{propsWithDefaults.currentPage === 1 ? "" :
			<button
				className={previousButtonClassName}
				onClick={() => propsWithDefaults.onPageChange(propsWithDefaults.currentPage - 1)}
				type="button">
				<div className={`ltgt ${propsWithDefaults.ltgtClassName}`}>
					&lt;
				</div>

				<div className={previousClassName}>
				  {t("businessDirectory.previous") ?? "Previous"}
				</div>
			</button>
		}
			{paginationValues.map((pageValue, nu) =>
				pageValue === separator ? (
					<div
						className={ellipsesClassName}
						key={nu}>
						...
					</div>
				) : (propsWithDefaults.currentPage === pageValue ? (
					<div
						className={currentPageValueClassName}
						key={nu}>
						{pageValue}
					</div>
				) : (
					<button
						className={propsWithDefaults.pageValueButtonClassName}
						key={nu}
						onClick={() => {
							propsWithDefaults.onPageChange(pageValue);
						}}
						type="button"
					>
						{pageValue}
					</button>
				))
			)}
			{propsWithDefaults.currentPage === paginationValues[paginationValues.length - 1] ? "" :
				<button
					className={nextButtonClassName}
					onClick={() => {
						propsWithDefaults.onPageChange(propsWithDefaults.currentPage + 1);
					}}
					type="button"
				>
					<div className={`ltgt ${propsWithDefaults.ltgtClassName}`}>
						&gt;
					</div>

					<div className={nextClassName}>
						{t("businessDirectory.next") ?? "Next"}
					</div>
				</button>
		}
		</div>
	);
};

export default PaginationElementComponent;
